/* src/components/Profile.js - v6.16 - AI Integration + Full Edit Features Preserved */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import Feed from './Feed';
import MENU_ITEMS from '../menuConfig.json'; 

function Profile({ 
    user, 
    targetUser, 
    posts, 
    onUpdateUser, 
    onNavigate, 
    onDeletePost, 
    onLikePost, 
    onAddComment, 
    onDeleteComment, 
    onEditComment, 
    onEditPost,
    onStartMessage,
    onStartAIChat, // NEW: AI Chat Prop
    onLogout,
    initialParams,
    onTriggerLogin,
    onNavigatePost, 
    onSearchTag 
}) {
  
  const profileUser = targetUser || user;
  
  // GUEST SAFE CHECK
  const isOwnProfile = user && (!targetUser || targetUser.name === user.name);
  const isNexus = profileUser.name.toLowerCase() === 'nexus'; // DETECT AI

  const [activeTab, setActiveTab] = useState('posts');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const deepLinkProcessed = useRef(false);

  // Edit State
  const [editHandle, setEditHandle] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editBanner, setEditBanner] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editPrivacy, setEditPrivacy] = useState('public');
  const [allowNexusMsgs, setAllowNexusMsgs] = useState(true); // NEW: Opt-out State
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [socials, setSocials] = useState({ website: '' });
  
  const [customMenu, setCustomMenu] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');

  // Muted Management State
  const [myMutedUsers, setMyMutedUsers] = useState([]);

  // NEW: Profile Menu State
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const profileMenuRef = useRef(null);

  const THEME_COLORS = [
      'rgba(43, 43, 54, 0.60)', 'rgba(0, 0, 0, 0.8)', 'rgba(255, 71, 87, 0.6)', 
      'rgba(255, 159, 67, 0.6)', 'rgba(255, 215, 0, 0.6)', 'rgba(46, 213, 115, 0.6)', 
      'rgba(0, 206, 201, 0.6)', 'rgba(30, 144, 255, 0.6)', 'rgba(5, 196, 107, 0.6)', 
      'rgba(138, 43, 226, 0.6)', 'rgba(255, 105, 180, 0.6)', 'rgba(87, 95, 207, 0.6)'
  ];

  const profilePosts = useMemo(() => posts.filter(p => p.username === profileUser.name), [posts, profileUser.name]);
  
  const galleryImages = useMemo(() => profilePosts.filter(p => p.image).map(p => ({
      id: p.id,
      url: p.image 
  })), [profilePosts]);

  const isFollowing = user && (user.friends || []).includes(profileUser.name);
  const visibleTabs = user ? ['posts', 'gallery', 'about'] : ['posts', 'about'];

  // Click Outside for Menu
  useEffect(() => {
    function handleClickOutside(event) {
        if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
            setShowProfileMenu(false);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- SAFE NAVIGATION HANDLER ---
  const handleProfileNavigation = (username) => {
      if (username === profileUser.name) return;
      onNavigate(username);
  };

  useEffect(() => {
      if (initialParams && initialParams.tab && !deepLinkProcessed.current) {
          if (initialParams.tab === 'gallery' && !user) {
              setActiveTab('posts');
          } else {
              setActiveTab(initialParams.tab);
              if (initialParams.tab === 'gallery' && initialParams.photoId) {
                  const idx = galleryImages.findIndex(p => p.id === parseInt(initialParams.photoId));
                  if (idx !== -1) setSelectedPhotoIndex(idx);
              }
          }
          deepLinkProcessed.current = true;
      }
  }, [initialParams, galleryImages, user]);
  
  const handleMuteUser = async () => {
    if (!user) return onTriggerLogin();
    if (!window.confirm(`Mute @${profileUser.name}? You won't see their content anymore.`)) return;
    
    try {
        const res = await fetch('/api/users/mute', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ current_user: user.name, target_user: profileUser.name })
        });
        const data = await res.json();
        alert(data.message);
        window.location.reload();
    } catch(e) { console.error(e); }
  };

  const handleReportUser = async () => {
    if (!reportReason) return alert("Please enter a reason.");
    try {
        await fetch('/api/report', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
                reporter: user.name, 
                target_id: `User Profile: ${profileUser.name}`, 
                reason: reportReason 
            })
        });
        
        setShowReportModal(false);
        setReportReason('');
        
        if (window.confirm("Report sent. Do you want to mute this user now?")) {
            await handleMuteUser();
        } else {
            alert("Report sent to admins.");
        }
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (isEditing && user) {
        setEditHandle(user.handle || '');
        setEditBio(user.bio || '');
        setEditBanner(user.banner_url || '');
        setEditColor(user.theme_color || 'rgba(43, 43, 54, 0.60)');
        setEditPrivacy(user.profile_privacy || 'public');
        setAllowNexusMsgs(user.nexus_opt_out !== true); // LOAD OPT-OUT PREFERENCE
        setSocials(user.social_links || { website: '' });
        setMyMutedUsers(user.muted_users || []); // Load muted list
        
        let layout = user.menu_layout || MENU_ITEMS.map(i => i.id);
        if (!layout.includes('profile')) layout = ['profile', ...layout];
        if (!layout.includes('home')) layout = [...layout, 'home'];
        
        setCustomMenu(layout);
    }
  }, [isEditing, user]);

  const handleToggleMenuItem = (id) => {
      if (id === 'profile' || id === 'home') return; 
      if (customMenu.includes(id)) {
          setCustomMenu(customMenu.filter(item => item !== id));
      } else {
          setCustomMenu([...customMenu, id]);
      }
  };

  const handleMoveItem = (index, direction) => {
      const newMenu = [...customMenu];
      if (direction === 'up' && index > 0) {
          [newMenu[index], newMenu[index - 1]] = [newMenu[index - 1], newMenu[index]];
      } else if (direction === 'down' && index < newMenu.length - 1) {
          [newMenu[index], newMenu[index + 1]] = [newMenu[index + 1], newMenu[index]];
      }
      setCustomMenu(newMenu);
  };

  const handleSave = async () => {
      if (newPassword && newPassword !== confirmPassword) return alert("Passwords do not match!");
      try {
          const res = await fetch('/api/user/update-full', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  id: user.id, 
                  handle: editHandle, 
                  bio: editBio,
                  banner_url: editBanner, 
                  theme_color: editColor,
                  profile_privacy: editPrivacy,
                  nexus_opt_out: !allowNexusMsgs, // SAVE OPT-OUT STATUS
                  social_links: socials, 
                  new_password: newPassword,
                  menu_layout: customMenu
              })
          });
          const data = await res.json();
          if (res.ok) { onUpdateUser(data.user); setIsEditing(false); } 
          else { alert(data.error); }
      } catch (e) { console.error(e); }
  };

  const handleUpload = async (e, type) => {
      const file = e.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
          const res = await fetch('/api/upload', { method: 'POST', body: formData });
          if (res.ok) {
              const data = await res.json();
              if (type === 'banner') setEditBanner(data.url);
              else if (type === 'pfp') {
                  const updateRes = await fetch('/api/user/update-full', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ id: user.id, profile_pic: data.url })
                  });
                  if (updateRes.ok) {
                      const updatedUser = await updateRes.json();
                      onUpdateUser(updatedUser.user);
                  }
              }
          }
      } catch (err) { console.error(err); }
  };

  const handleCustomColor = (e) => {
      const hex = e.target.value;
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      setEditColor(`rgba(${r}, ${g}, ${b}, 0.75)`);
  };

  const handleFollowToggle = async () => {
      if(!user) { if(onTriggerLogin) onTriggerLogin(); return; }
      try {
          const res = await fetch('/api/users/friend', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ current_user: user.name, target_user: profileUser.name })
          });
          if (res.ok) alert("Friend list updated! (Refresh to see changes)");
      } catch (e) { console.error(e); }
  };

  // --- SPECIAL NEXUS BUTTON HANDLER ---
  const handleMessageClick = () => {
      if(!user) { if(onTriggerLogin) onTriggerLogin(); return; }
      
      if (isNexus && onStartAIChat) {
          onStartAIChat(); // Launch AI Interface
      } else {
          onStartMessage(profileUser.name); // Launch Regular Chat
      }
  };

  const handleDeleteAccount = async () => {
      if (!deletePassword) return alert("Please enter your password.");
      try {
          const res = await fetch('/api/user/delete-request', {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username: user.name, password: deletePassword })
          });
          const data = await res.json();
          if (res.ok) {
              alert("Account Scheduled for Deletion.\nLog in anytime within 30 days to cancel.");
              onLogout();
          } else {
              alert(data.error || "Failed.");
          }
      } catch (e) { alert("Server error."); }
  };

  const nextPhoto = (e) => { e.stopPropagation(); setSelectedPhotoIndex((prev) => (prev + 1) % galleryImages.length); };
  const prevPhoto = (e) => { e.stopPropagation(); setSelectedPhotoIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length); };

  // --- NEW UNMUTE HANDLER ---
  const handleUnmute = async (target) => {
      try {
          const res = await fetch('/api/users/mute', { 
              method: 'POST', headers: { 'Content-Type': 'application/json' }, 
              body: JSON.stringify({ current_user: user.name, target_user: target }) 
          });
          const data = await res.json();
          setMyMutedUsers(data.muted_list || []);
      } catch (e) { console.error(e); }
  };

  if (isEditing) {
      return (
          <div className="card" style={{ padding: 0, overflow: 'hidden', position: 'relative' }}>
              <div style={{ height: '180px', background: editBanner ? `url(${editBanner}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)', position: 'relative' }}>
                  <button onClick={() => setIsEditing(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', borderRadius: '50%', width: '30px', height: '30px', cursor: 'pointer' }}>✕</button>
                  <button onClick={onLogout} style={{ position: 'absolute', top: '15px', left: '15px', background: 'rgba(255, 71, 87, 0.2)', border: '1px solid #ff4757', color: '#ff4757', padding: '5px 15px', borderRadius: '20px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem', backdropFilter: 'blur(4px)' }}>🛑 Logout</button>
              </div>
              <div style={{ padding: '20px' }}>
                  <h2 style={{ textAlign: 'center', marginBottom: '20px', letterSpacing: '2px', textTransform: 'uppercase' }}>Profile Design</h2>
                  
                  {/* NAVIGATION CUSTOMIZATION */}
                  <div style={{ marginBottom: '30px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '10px' }}>
                      <h4 style={{ textAlign: 'center', margin: '0 0 15px 0', color: '#74b9ff', textTransform: 'uppercase', fontSize: '0.9rem' }}>Customize Navigation</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {customMenu.map((id, index) => {
                              const item = MENU_ITEMS.find(i => i.id === id);
                              if (!item) return null;
                              const isMandatory = id === 'profile' || id === 'home';
                              return (
                                  <div key={id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '5px' }}>
                                      <input 
                                        type="checkbox" 
                                        checked={true} 
                                        disabled={isMandatory}
                                        onChange={() => handleToggleMenuItem(id)} 
                                        style={{ cursor: isMandatory ? 'not-allowed' : 'pointer', opacity: 1 }} 
                                      />
                                      <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                                      <span style={{ flex: 1, fontWeight: 'bold' }}>{item.label} {isMandatory && <span style={{fontSize:'0.7rem', color:'#777', fontWeight:'normal'}}>(Fixed)</span>}</span>
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                          <button onClick={() => handleMoveItem(index, 'up')} disabled={index === 0} style={{ border: 'none', background: 'none', color: index === 0 ? '#555' : 'white', cursor: 'pointer', fontSize: '0.7rem' }}>▲</button>
                                          <button onClick={() => handleMoveItem(index, 'down')} disabled={index === customMenu.length - 1} style={{ border: 'none', background: 'none', color: index === customMenu.length - 1 ? '#555' : 'white', cursor: 'pointer', fontSize: '0.7rem' }}>▼</button>
                                      </div>
                                  </div>
                              );
                          })}
                          
                          {MENU_ITEMS.filter(i => !customMenu.includes(i.id)).map(item => (
                              <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.05)', padding: '8px', borderRadius: '5px', opacity: 0.6 }}>
                                  <input type="checkbox" checked={false} onChange={() => handleToggleMenuItem(item.id)} style={{ cursor: 'pointer' }} />
                                  <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
                                  <span style={{ flex: 1 }}>{item.label}</span>
                              </div>
                          ))}
                      </div>
                  </div>

                  {/* IMAGES */}
                  <div style={{ marginBottom: '20px', display:'flex', gap:'20px', justifyContent:'center', flexWrap:'wrap' }}>
                      <div style={{ textAlign: 'center' }}>
                          <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>BANNER</label>
                          <label style={{ background: '#333', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9rem', display:'block', marginBottom:'5px' }}>
                              Change Banner
                              <input type="file" hidden onChange={(e) => handleUpload(e, 'banner')} />
                          </label>
                          <button onClick={() => setEditBanner('')} style={{ background: '#ff4757', border: 'none', color: 'white', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize:'0.8rem' }}>Remove</button>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                          <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>AVATAR</label>
                          <label style={{ background: '#333', padding: '8px 15px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.9rem', display:'block' }}>
                              Change Photo
                              <input type="file" hidden onChange={(e) => handleUpload(e, 'pfp')} />
                          </label>
                      </div>
                  </div>

                  {/* COLORS */}
                  <div style={{ marginBottom: '30px', textAlign: 'center' }}>
                      <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '10px' }}>CARD BACKGROUND COLOR</label>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                          {THEME_COLORS.map(c => (
                              <div key={c} onClick={() => setEditColor(c)} style={{ width: '35px', height: '35px', borderRadius: '50%', background: c, border: editColor === c ? '2px solid white' : '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.3)' }} />
                          ))}
                          <label style={{ width: '35px', height: '35px', borderRadius: '50%', background: 'linear-gradient(135deg, red, orange, yellow, green, blue, purple)', border: '1px solid rgba(255,255,255,0.2)', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Custom Color">
                              <input type="color" style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }} onChange={handleCustomColor} />
                              <span style={{ fontSize: '10px', color: 'white', textShadow: '0 0 2px black', pointerEvents: 'none' }}>+</span>
                          </label>
                      </div>
                  </div>

                  <hr style={{ borderColor: 'rgba(255,255,255,0.1)', margin: '20px 0' }} />
                  <h3 style={{ textAlign: 'center', marginBottom: '20px', letterSpacing: '2px', textTransform: 'uppercase' }}>Identity & Preferences</h3>
                  
                  {/* --- NEW MUTED USERS SECTION --- */}
                  <div style={{ background: 'rgba(0,0,0,0.2)', padding:'15px', borderRadius:'10px', marginBottom: '20px' }}>
                      <label style={{ display: 'block', color: '#74b9ff', fontSize: '0.9rem', marginBottom: '10px', textTransform:'uppercase' }}>Blocked / Muted Users</label>
                      {myMutedUsers.length === 0 && <div style={{ color: '#aaa', fontSize: '0.9rem', fontStyle: 'italic' }}>You have not muted anyone.</div>}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {myMutedUsers.map(mUser => (
                              <div key={mUser} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.05)', padding: '8px 12px', borderRadius: '5px' }}>
                                  <span style={{ fontWeight: 'bold' }}>@{mUser}</span>
                                  <button onClick={() => handleUnmute(mUser)} style={{ background: 'transparent', border: '1px solid #777', color: '#ccc', padding: '2px 10px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem' }}>Unmute</button>
                              </div>
                          ))}
                      </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div>
                          <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>DISPLAY HANDLE</label>
                          <input className="post-input" value={editHandle} onChange={(e) => setEditHandle(e.target.value)} placeholder="@cooluser" />
                      </div>
                      
                      <div>
                          <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>BIO</label>
                          <textarea className="post-input" value={editBio} onChange={(e) => setEditBio(e.target.value)} placeholder="Tell us about yourself..." style={{ minHeight: '80px' }} />
                      </div>

                      {/* NEW PREFERENCES SECTION */}
                      <div style={{ background: 'rgba(0,0,0,0.2)', padding:'15px', borderRadius:'10px' }}>
                          <label style={{ display: 'block', color: '#74b9ff', fontSize: '0.9rem', marginBottom: '10px', textTransform:'uppercase' }}>Privacy & Notifications</label>
                          
                          <div style={{marginBottom:'10px'}}>
                              <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>PROFILE VISIBILITY</label>
                              <select className="post-input" value={editPrivacy} onChange={(e) => setEditPrivacy(e.target.value)} style={{ width: '100%', background: '#333', color: 'white' }}>
                                  <option value="public">🌍 Public (Everyone)</option>
                                  <option value="friends">👥 Friends Only</option>
                                  <option value="private">🔒 Private (Only Me)</option>
                              </select>
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <input 
                                  type="checkbox" 
                                  checked={allowNexusMsgs} 
                                  onChange={(e) => setAllowNexusMsgs(e.target.checked)} 
                                  style={{ width: '20px', height: '20px', cursor:'pointer' }}
                              />
                              <div>
                                  <div style={{ fontWeight: 'bold' }}>Receive messages from Nexus AI</div>
                                  <div style={{ fontSize: '0.8rem', color: '#aaa' }}>Allow the system AI to send you tips and greetings.</div>
                              </div>
                          </div>
                      </div>

                      <div>
                          <label style={{ display: 'block', color: '#a0a0b0', fontSize: '0.8rem', marginBottom: '5px' }}>WEBSITE</label>
                          <input className="post-input" value={socials.website} onChange={(e) => setSocials({...socials, website: e.target.value})} placeholder="https://" />
                      </div>
                      <div style={{ borderTop:'1px solid rgba(255,255,255,0.1)', paddingTop:'15px', marginTop:'10px' }}>
                          <label style={{ display: 'block', color: '#ff4757', fontSize: '0.8rem', marginBottom: '10px' }}>CHANGE PASSWORD (OPTIONAL)</label>
                          <input className="post-input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New Password" style={{marginBottom:'10px'}} />
                          <input className="post-input" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm New Password" />
                      </div>
                  </div>
                  <div style={{ marginTop: '30px', textAlign: 'right' }}>
                      <button onClick={handleSave} style={{ background: '#2ed573', color: 'white', border: 'none', padding: '10px 30px', borderRadius: '30px', fontWeight: 'bold', cursor: 'pointer', fontSize: '1rem' }}>Save Profile</button>
                  </div>

                  {/* DANGER ZONE */}
                  <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <h4 style={{ color: '#ff4757', margin: '0 0 10px 0' }}>Danger Zone</h4>
                      <button onClick={() => setShowDeleteModal(true)} style={{ background: 'rgba(255, 71, 87, 0.1)', border: '1px solid #ff4757', color: '#ff4757', padding: '10px', width: '100%', borderRadius: '5px', cursor: 'pointer' }}>Delete Account</button>
                  </div>
              </div>

              {showDeleteModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div className="card" style={{ width: '90%', maxWidth: '400px', padding: '20px', border: '1px solid #ff4757' }}>
                        <h3 style={{ color: '#ff4757', marginTop: 0 }}>Confirm Deletion</h3>
                        <p style={{ fontSize: '0.9rem', color: '#ccc' }}>
                            This will schedule your account for permanent deletion in 30 days. You can cancel this by logging back in at any time before then.
                        </p>
                        <p style={{ fontSize: '0.9rem', marginBottom: '15px' }}>Enter your password to confirm:</p>
                        <input type="password" placeholder="Password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} style={{ width: '100%', padding: '10px', marginBottom: '20px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white' }} />
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button onClick={() => { setShowDeleteModal(false); setDeletePassword(''); }} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleDeleteAccount} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Schedule Deletion</button>
                        </div>
                    </div>
                </div>
              )}
          </div>
      );
  }

  const themeStyle = {
      background: profileUser.theme_color || 'rgba(43, 43, 54, 0.60)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.05)'
  };

  return (
    <div className="feed-container">
      <div className="card" style={{ padding: 0, overflow: 'visible', position: 'relative', marginTop: 0, ...themeStyle }}>
        
        {/* DELETION BANNER */}
        {profileUser.scheduled_deletion && (
            <div style={{ background: '#ff4757', color: 'white', textAlign: 'center', padding: '10px', fontWeight: 'bold', fontSize: '0.9rem', borderRadius: '15px 15px 0 0' }}>
                ⚠️ This user scheduled to have their profile deleted.
            </div>
        )}
        
        {/* BANNER + MENU */}
        <div style={{ height: '200px', background: profileUser.banner_url ? `url(${profileUser.banner_url}) center/cover` : 'linear-gradient(45deg, #1e90ff, #2ed573)', borderRadius: profileUser.scheduled_deletion ? '0' : '15px 15px 0 0', position: 'relative' }}>
            
            {/* OWNER EDIT */}
            {isOwnProfile && (
                <button onClick={() => setIsEditing(true)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '20px', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>✎ Edit</button>
            )}
        
            {/* OTHER USER: 3-DOT MENU */}
            {!isOwnProfile && user && (
                <div ref={profileMenuRef} style={{ position: 'absolute', top: '15px', right: '15px', zIndex: 100 }}>
                    <button onClick={() => setShowProfileMenu(!showProfileMenu)} style={{ background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '18px', display:'flex', alignItems:'center', justifyContent:'center' }}>⋮</button>
                    {showProfileMenu && (
                        <div style={{ position: 'absolute', right: 0, top: '35px', background: '#2f3542', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '5px', width: '140px', overflow: 'hidden', boxShadow: '0 5px 15px rgba(0,0,0,0.5)' }}>
                            <button onClick={() => { setShowReportModal(true); setShowProfileMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ff4757', cursor: 'pointer', fontSize:'0.9rem' }}>🚩 Report User</button>
                            <button onClick={() => { handleMuteUser(); setShowProfileMenu(false); }} style={{ display: 'block', width: '100%', padding: '10px', textAlign: 'left', background: 'transparent', border: 'none', color: '#ccc', cursor: 'pointer', fontSize:'0.9rem' }}>🔇 Mute User</button>
                        </div>
                    )}
                </div>
            )}
        </div>
        
        {/* REPORT MODAL */}
        {showReportModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.8)', zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <div className="card" style={{ width: '90%', maxWidth: '400px', border: '1px solid #ff4757' }}>
                  <h3 style={{ marginTop: 0, color: '#ff4757' }}>Report User</h3>
                  <p style={{ fontSize: '0.9rem', color: '#ccc' }}>Why are you reporting {profileUser.name}?</p>
                  <textarea value={reportReason} onChange={(e) => setReportReason(e.target.value)} placeholder="Harassment, fake account, spam..." style={{ width: '100%', height: '80px', padding: '10px', borderRadius: '5px', border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', marginBottom: '15px' }} />
                  <div style={{ display: 'flex', gap: '10px' }}>
                      <button onClick={() => setShowReportModal(false)} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #aaa', color: '#aaa', borderRadius: '5px', cursor: 'pointer' }}>Cancel</button>
                      <button onClick={handleReportUser} style={{ flex: 1, padding: '10px', background: '#ff4757', border: 'none', color: 'white', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}>Submit Report</button>
                  </div>
              </div>
          </div>
        )}
        
        <div className="profile-info" style={{ position: 'relative' }}>
            <div className="profile-avatar" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: '-60px', borderRadius: '50%', background: '#111', padding: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 20px rgba(0,0,0,0.3)' }}>
                {profileUser.profile_pic ? (
                    <img src={profileUser.profile_pic} alt="Profile" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                ) : (
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: profileUser.avatarColor, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '50px', fontWeight: 'bold', color: 'white' }}>{profileUser.name[0].toUpperCase()}</div>
                )}
            </div>
            
            {/* (Stats and Info - Same as before) */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: '10px', minHeight: '60px' }}>
                <div style={{ textAlign: 'left', maxWidth: '35%' }}>
                    <h2 style={{ margin: 0, fontSize: '1.8rem', lineHeight: '1.1', wordBreak: 'break-word' }}>{profileUser.name}</h2>
                    <span style={{ color: '#2ed573', fontWeight: 'bold', fontSize: '0.9rem' }}>{profileUser.handle}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                    <div style={{ display: 'flex', gap: '15px', textAlign: 'center' }}>
                        <div><div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{profilePosts.length}</div><div style={{ fontSize: '0.75rem', color: '#aaa' }}>Posts</div></div>
                        <div><div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{(profileUser.friends || []).length}</div><div style={{ fontSize: '0.75rem', color: '#aaa' }}>Friends</div></div>
                    </div>
                    {user && !isOwnProfile && (
                        <button onClick={handleFollowToggle} style={{ marginTop: '5px', background: isFollowing ? 'transparent' : '#2ed573', border: isFollowing ? '1px solid rgba(255,255,255,0.5)' : 'none', color: 'white', padding: '5px 15px', borderRadius: '15px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold', transition: 'all 0.2s' }}>{isFollowing ? 'Following' : 'Follow'}</button>
                    )}
                </div>
            </div>



            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <p style={{ lineHeight: '1.5', color: '#ddd', maxWidth: '600px', margin: '0 auto' }}>{profileUser.bio || "No bio yet."}</p>
                {profileUser.social_links?.website && <a href={profileUser.social_links.website} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: '10px', color: '#1e90ff', textDecoration: 'none' }}>🔗 {profileUser.social_links.website}</a>}
                
                {/* NEXUS AI BUTTON INTEGRATION */}
                {user && !isOwnProfile && (
                    <div style={{ marginTop: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                        <button 
                            onClick={handleMessageClick} 
                            style={{ 
                                background: isNexus ? 'linear-gradient(45deg, #a55eea, #45aaf2)' : '#1e90ff', 
                                color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', 
                                cursor: 'pointer', fontWeight: 'bold', display:'flex', alignItems:'center', gap:'8px',
                                boxShadow: isNexus ? '0 0 15px rgba(165, 94, 234, 0.5)' : 'none'
                            }}
                        >
                            {isNexus ? (<span>✨ Chat with AI</span>) : ('Message')}
                        </button>
                    </div>
                )}
            </div>
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            {visibleTabs.map(tab => (
                <button 
                    key={tab}
                    onClick={() => setActiveTab(tab)} 
                    style={{ 
                        flex: 1, padding: '20px', 
                        background: activeTab === tab ? 'rgba(255,255,255,0.05)' : 'transparent', 
                        border: 'none', 
                        color: activeTab === tab ? (tab === 'gallery' ? '#1e90ff' : '#2ed573') : '#aaa', 
                        cursor: 'pointer', fontWeight: 'bold', textTransform: 'uppercase' 
                    }}
                >
                    {tab}
                </button>
            ))}
        </div>
      </div>
      <div style={{ marginTop: '20px' }}>
          {activeTab === 'posts' && (
              <Feed 
                user={user} 
                posts={profilePosts} 
                onAddPost={() => {}} 
                onDeletePost={onDeletePost} 
                onLikePost={onLikePost} 
                onAddComment={onAddComment} 
                onDeleteComment={onDeleteComment} 
                onEditComment={onEditComment} 
                onEditPost={onEditPost} 
                onNavigateProfile={handleProfileNavigation}
                onNavigatePost={onNavigatePost}
                onSearchTag={onSearchTag}
                showInput={false} 
                onTriggerLogin={onTriggerLogin} 
              />
          )}
          {activeTab === 'gallery' && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px' }}>
                  {galleryImages.length > 0 ? galleryImages.map((p, index) => (
                      <div key={p.id} onClick={() => setSelectedPhotoIndex(index)} style={{ aspectRatio: '1/1', background: `url(${p.url}) center/cover`, borderRadius: '10px', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)' }}></div>
                  )) : <div style={{ color: '#aaa', padding: '20px' }}>No photos found.</div>}
              </div>
          )}
          {activeTab === 'about' && (
              <div className="card" style={{ padding: '30px', color: '#ccc', lineHeight: '1.6' }}>
                  <h3>About {profileUser.name}</h3>
                  <p>Joined: {new Date().toLocaleDateString()}</p>
                  <p>Reputation: {profilePosts.reduce((acc, p) => acc + (p.likes || 0), 0)} Likes</p>
              </div>
          )}
      </div>

      {selectedPhotoIndex !== null && (
          <div onClick={() => setSelectedPhotoIndex(null)} style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <button onClick={prevPhoto} style={{ position: 'absolute', left: '20px', background: 'none', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer', padding: '20px', zIndex: 10001 }}>❮</button>
              <img src={galleryImages[selectedPhotoIndex].url} style={{ maxWidth: '90%', maxHeight: '90%', borderRadius: '10px', boxShadow: '0 0 50px rgba(0,0,0,0.5)' }} alt="Fullscreen" onClick={(e) => e.stopPropagation()} />
              <button onClick={nextPhoto} style={{ position: 'absolute', right: '20px', background: 'none', border: 'none', color: 'white', fontSize: '40px', cursor: 'pointer', padding: '20px', zIndex: 10001 }}>❯</button>
          </div>
      )}

      <style>{`
        .profile-info { padding: 0 30px 30px 30px; }
        .profile-avatar { width: 120px; height: 120px; }
        @media (max-width: 600px) {
            .profile-info { padding: 0 10px 20px 10px !important; }
            .profile-avatar { width: 100px !important; height: 100px !important; top: -50px !important; }
        }
      `}</style>
    </div>
  );
}

export default Profile;
